
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart, ShippingMethodType } from '../../state/CartContext';
import { useAuth } from '../../state/auth';
import { SimpleHeader } from '../../components/SimpleHeader';
import { useToast } from '../../state/ToastContext';
import { catalogService } from '../../domain/services/catalogService';
import { Product } from '../../domain/types';
import { HorizontalProductList } from '../../components/HorizontalProductList';
import { useMarketing } from '../../state/MarketingContext';
import { CartShippingCalculator } from './components/CartShippingCalculator';
import { shippingService } from '../../domain/services/shippingService';
import { PRICING, DISPLAY_LIMITS, DEFAULTS } from '../../domain/constants';
import { SCROLL } from '../../domain/constants/ui';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

export const CartScreen = () => {
     const navigate = useNavigate();
     const { 
         items, addToCart, decreaseQuantity, removeFromCart, 
         total, couponCode, discountAmount, applyCoupon, removeCoupon,
         setShippingCost, setShippingMethod, shippingMethod, shippingCost,
         zipCode, setZipCode
     } = useCart();
     const { user } = useAuth();
     const { addToast } = useToast();
     const { shippingCosts } = useMarketing();
     
     const [recommended, setRecommended] = useState<Product[]>([]);
     const [impulseItems, setImpulseItems] = useState<Product[]>([]);
     const [couponInput, setCouponInput] = useState('');
     
     // Shipping Calculator State
     const [calcZip, setCalcZip] = useState(zipCode);
     const [availableMethods, setAvailableMethods] = useState<{id: ShippingMethodType, name: string, price: number, eta: string}[]>([]);
     
     // Impulse Slider Refs & State
     const sliderRef = useRef<HTMLDivElement>(null);
     const [showLeftArrow, setShowLeftArrow] = useState(false);
     const [showRightArrow, setShowRightArrow] = useState(true);
     
     const finalTotal = useMemo(() => Math.max(0, total - discountAmount) + shippingCost, [total, discountAmount, shippingCost]);

      useEffect(() => {
          const cartIds = items.map(i => i.product.id);
          catalogService.getRecommendedProducts(cartIds).then(setRecommended);

          catalogService.getAllProductsForDisplay(100).then(all => {
              const cheapItems = all
                 .filter(p => !cartIds.includes(p.id) && p.price < PRICING.IMPULSE_THRESHOLD)
                 .sort(() => 0.5 - Math.random())
                 .slice(0, DISPLAY_LIMITS.CART_SUGGESTIONS);
             setImpulseItems(cheapItems);
         });
         
         // Auto-fill ZIP if logged in and not set
          if (user && (user.addresses?.length || 0) > 0 && !zipCode) {
              setCalcZip(DEFAULTS.ZIP_CODE); 
              calculateShipping(DEFAULTS.ZIP_CODE);
          } else if (zipCode) {
             calculateShipping(zipCode);
         }
     }, [items, user]);

     const checkScroll = () => {
        if (sliderRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (sliderRef.current) {
            sliderRef.current.scrollBy({
                left: direction === 'left' ? -SCROLL.SCROLL_AMOUNT : SCROLL.SCROLL_AMOUNT,
                behavior: 'smooth'
            });
        }
    };

     const handleCouponSubmit = () => {
         if (!couponInput) return;
         const success = applyCoupon(couponInput);
         if (success) {
             addToast('¡Cupón aplicado con éxito!', 'success');
             setCouponInput('');
         } else {
             addToast('Cupón inválido o expirado (Prueba: HOLA-ALLTUC)', 'error');
         }
     };

     // Shipping Logic - Refactored to use Service
     const calculateShipping = (zip: string) => {
         if (!zip) return;
         
         // Delegate business logic to service
         const options = shippingService.getOptionsForZip(zip, shippingCosts);
         setAvailableMethods(options);
         setZipCode(zip);
     };

     const handleMethodSelect = (methodId: ShippingMethodType) => {
         const selected = availableMethods.find(m => m.id === methodId);
         if (selected) {
             setShippingMethod(methodId);
             setShippingCost(selected.price);
         }
     };

     const handleContinue = () => {
         // Smart Navigation Logic
         const hasShipping = shippingMethod !== null;
         const hasAddress = user && user.addresses.length > 0;
         
         if (hasShipping && hasAddress && shippingMethod !== 'pickup') {
             // Everything ready, skip to payment
             navigate('/payment');
         } else if (hasShipping && shippingMethod === 'pickup') {
             // Pickup doesn't strictly need address, go to payment
             navigate('/payment');
         } else {
             // Needs address details or shipping selection
             navigate('/shipping');
         }
     };

     return (
         <div className="relative flex h-full min-h-screen w-full flex-col overflow-x-hidden md:max-w-xl lg:max-w-2xl mx-auto bg-background-light dark:bg-background-dark shadow-2xl border-x border-black/5 dark:border-white/5">
            <SimpleHeader title={`Carrito (${items.length})`} />
            
            <main className="flex-1 px-4 py-2 space-y-6 pb-40">
                {items.length === 0 ? (
                    <div className="h-[60vh] flex flex-col items-center justify-center text-text-secondary dark:text-gray-500 gap-6 animate-fade-in">
                        <div className="w-24 h-24 bg-black/5 dark:bg-white/5 rounded-full flex items-center justify-center relative">
                            <span className="material-symbols-outlined text-5xl opacity-50">shopping_cart_off</span>
                            {/* Visual Indicator if Coupon is waiting */}
                            {couponCode && (
                                <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded-full animate-bounce shadow-lg">
                                    Cupón Activo
                                </div>
                            )}
                        </div>
                        <div className="text-center">
                            <p className="text-lg font-bold text-text-main dark:text-white">Tu carrito está vacío</p>
                            {couponCode ? (
                                <p className="text-sm text-green-600 dark:text-green-400 font-bold mt-1">¡El cupón {couponCode} se aplicará a tu compra!</p>
                            ) : (
                                <p className="text-sm">¡Tenemos miles de ofertas esperándote!</p>
                            )}
                        </div>
                        <Button onClick={() => navigate('/home')}>
                            Ir a comprar
                        </Button>
                    </div>
                ) : (
                <>
                    <div className="space-y-3 mt-2 animate-fade-in">
                        {items.map((item) => (
                            <div key={item.product.id} className="flex gap-4 bg-surface-light dark:bg-surface-dark p-3 rounded-xl shadow-sm border border-black/5 dark:border-white/5 items-center justify-between group">
                                {/* Clickable Area */}
                                <div 
                                    onClick={() => navigate(`/product/${item.product.id}`)}
                                    className="flex items-center gap-4 flex-1 overflow-hidden cursor-pointer"
                                >
                                    <div className="bg-center bg-no-repeat aspect-square bg-cover rounded-lg size-[80px] shrink-0" style={{backgroundImage: `url("${item.product.coverImageUrl || item.product.imageUrl || (item.product.images && item.product.images[0]) || 'https://placehold.co/80'}")`}}></div>
                                    <div className="flex flex-col justify-center gap-1 min-w-0">
                                        <p className="text-text-main dark:text-white text-base font-bold leading-tight line-clamp-1 truncate group-hover:text-primary transition-colors">{item.product.title}</p>
                                        <p className="text-primary font-bold text-sm leading-normal">$ {item.product.price.toLocaleString('es-AR')}</p>
                                    </div>
                                </div>
                                <div className="shrink-0 flex flex-col items-end gap-2">
                                     <Button onClick={() => removeFromCart(item.product.id)} variant="ghost" size="sm" className="text-gray-400 hover:text-red-500 p-1">
                                         <span className="material-symbols-outlined text-[18px]">delete</span>
                                     </Button>
                                     <div className="flex items-center rounded-full bg-black/5 dark:bg-white/5 p-1">
                                         <Button onClick={() => decreaseQuantity(item.product.id)} variant="ghost" size="sm" className="h-7 w-7 rounded-full bg-white dark:bg-white/10 text-text-main dark:text-white p-0">
                                             <span className="material-symbols-outlined text-[16px]">remove</span>
                                         </Button>
                                         <span className="w-8 text-center font-bold text-sm text-text-main dark:text-white">{item.quantity}</span>
                                         <Button onClick={() => addToCart(item.product)} variant="ghost" size="sm" className="h-7 w-7 rounded-full bg-white dark:bg-white/10 text-text-main dark:text-white p-0">
                                             <span className="material-symbols-outlined text-[16px]">add</span>
                                         </Button>
                                     </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Button onClick={() => navigate('/catalog')} variant="ghost" className="w-full py-3 text-primary font-bold text-sm border border-primary/20 rounded-xl hover:bg-primary/5">
                        Ver más productos
                    </Button>
                
                    {/* IMPULSE BUY SLIDER */}
                    {impulseItems.length > 0 && (
                        <div className="animate-fade-in relative group/impulse pt-2">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="material-symbols-outlined text-green-500">savings</span>
                                <h3 className="font-bold text-sm text-text-main dark:text-white uppercase tracking-wide">Agregá por menos de $10.000</h3>
                            </div>
                            
                            <div className="relative">
                                {/* Left Arrow - Larger & Inset */}
                                <Button 
                                    onClick={() => scroll('left')}
                                    variant="ghost"
                                    className={`absolute left-2 top-1/2 -translate-y-1/2 z-30 w-14 h-14 bg-white/90 dark:bg-black/80 backdrop-blur-md rounded-full shadow-xl border border-black/5 dark:border-white/10 text-primary transition-all duration-300 hover:scale-110 active:scale-95 p-0 ${showLeftArrow ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8 pointer-events-none'}`}
                                >
                                    <span className="material-symbols-outlined text-4xl">chevron_left</span>
                                </Button>

                                {/* Right Arrow - Larger & Inset */}
                                <Button 
                                    onClick={() => scroll('right')}
                                    variant="ghost"
                                    className={`absolute right-2 top-1/2 -translate-y-1/2 z-30 w-14 h-14 bg-white/90 dark:bg-black/80 backdrop-blur-md rounded-full shadow-xl border border-black/5 dark:border-white/10 text-primary transition-all duration-300 hover:scale-110 active:scale-95 p-0 ${showRightArrow ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none'}`}
                                >
                                    <span className="material-symbols-outlined text-4xl">chevron_right</span>
                                </Button>

                                <div 
                                    ref={sliderRef} 
                                    onScroll={checkScroll}
                                    className="flex gap-3 overflow-x-auto no-scrollbar pb-2 scroll-smooth snap-x snap-mandatory px-1"
                                >
                                    {impulseItems.map(item => (
                                        <div 
                                            key={item.id} 
                                            onClick={() => navigate(`/product/${item.id}`)}
                                            className="min-w-[140px] w-[140px] bg-surface-light dark:bg-surface-dark p-3 rounded-xl border border-black/5 dark:border-white/5 flex flex-col gap-2 relative cursor-pointer hover:border-primary/30 transition-colors shrink-0 snap-start"
                                        >
                                            <div className="w-full h-24 bg-center bg-cover rounded-lg bg-gray-100" style={{backgroundImage: `url("${item.imageUrl}")`}}></div>
                                            <div className="flex-1">
                                                <p className="text-xs font-bold line-clamp-1 text-text-main dark:text-white">{item.title}</p>
                                                <p className="text-primary font-bold text-sm">$ {item.price.toLocaleString()}</p>
                                            </div>
                                            <Button 
                                                onClick={(e) => { 
                                                    e.stopPropagation();
                                                    addToCart(item); 
                                                    addToast(`Agregaste ${item.title}`, 'success');
                                                }} 
                                                variant="ghost"
                                                size="sm"
                                                className="w-full py-1.5 bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white text-text-secondary dark:text-white text-xs font-bold rounded-lg"
                                            >
                                                Agregar
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="bg-surface-light dark:bg-surface-dark rounded-xl p-5 shadow-sm border border-black/5 dark:border-white/5 space-y-4">
                        
                        <CartShippingCalculator 
                            calcZip={calcZip}
                            setCalcZip={setCalcZip}
                            calculateShipping={calculateShipping}
                            availableMethods={availableMethods}
                            shippingMethod={shippingMethod}
                            handleMethodSelect={handleMethodSelect}
                        />

                        {/* 2. COUPONS */}
                        {!couponCode ? (
                            <div className="flex gap-2">
                                <Input
                                    type="text"
                                    placeholder="Código de cupón"
                                    value={couponInput}
                                    onChange={(e) => setCouponInput(e.target.value)}
                                    className="flex-1"
                                />
                                <Button onClick={handleCouponSubmit} size="sm">
                                    Aplicar
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between bg-green-100 dark:bg-green-900/20 p-2 rounded-lg border border-green-200 dark:border-green-800">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-green-600 dark:text-green-400">local_offer</span>
                                    <div>
                                        <p className="text-xs font-bold text-green-700 dark:text-green-300">Cupón: {couponCode}</p>
                                        <p className="text-[10px] text-green-600 dark:text-green-400">
                                            {discountAmount > 0 ? 'Descuento aplicado' : 'Se aplicará en el siguiente paso'}
                                        </p>
                                    </div>
                                </div>
                                <Button onClick={removeCoupon} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 p-1">
                                    <span className="material-symbols-outlined text-[18px]">close</span>
                                </Button>
                            </div>
                        )}

                        {/* 3. TOTALS */}
                        <div className="pt-2 border-t border-black/5 dark:border-white/10">
                            <div className="flex justify-between gap-x-6 py-1">
                                <p className="text-text-secondary dark:text-gray-400 text-sm">Subtotal</p>
                                <p className="text-text-main dark:text-white text-sm font-medium">$ {total.toLocaleString('es-AR')}</p>
                            </div>
                            
                            {discountAmount > 0 && (
                                <div className="flex justify-between gap-x-6 py-1 text-green-600 dark:text-green-400 animate-slide-up">
                                    <p className="text-sm flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[16px]">local_offer</span>
                                        Descuento
                                    </p>
                                    <p className="text-sm font-bold">- $ {discountAmount.toLocaleString('es-AR')}</p>
                                </div>
                            )}

                            {shippingMethod && (
                                <div className="flex justify-between gap-x-6 py-1 animate-slide-up">
                                    <p className="text-text-secondary dark:text-gray-400 text-sm">Envío</p>
                                    <p className="text-text-main dark:text-white text-sm font-bold">{shippingCost === 0 ? 'GRATIS' : `$ ${shippingCost.toLocaleString()}`}</p>
                                </div>
                            )}

                            <div className="flex justify-between gap-x-6 pt-4 items-end">
                                <p className="text-text-main dark:text-white text-base font-bold">Total Final</p>
                                <p className="text-primary text-2xl font-black tracking-tight">$ {finalTotal.toLocaleString('es-AR')}</p>
                            </div>
                        </div>
                    </div>

                    {recommended.length > 0 && (
                        <div className="pb-4">
                             <HorizontalProductList 
                                title="Podría interesarte"
                                products={recommended}
                                isLoading={false}
                                seeAllLink=""
                             />
                        </div>
                    )}
                </>
                )}
            </main>

            {items.length > 0 && (
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-md border-t border-black/5 dark:border-white/5 flex justify-center z-20 w-full md:max-w-xl lg:max-w-2xl mx-auto pb-safe">
                <Button onClick={handleContinue} className="w-full h-12 rounded-full">
                    Continuar Compra
                    <span className="material-symbols-outlined ml-2 text-[20px]">arrow_forward</span>
                </Button>
            </div>
            )}
         </div>
     )
}
